import Typography from '@ixigo/iui/Typography';
import { useSearch } from '@tanstack/react-router';
import { Fragment, useEffect, useRef } from 'react';
import { ContactDetail } from '@/components/contact-detail';
import {
	FarePolicies,
	FareSummary,
	getFareLineItems,
	getPartialPayFareRows,
} from '@/components/fare-summary';
import { SectionHeader } from '@/components/section-header';
import { TripDetailsCard } from '@/components/trip-details-card';
import { SUCCESS_BOOKING_STATUS } from '@/routes/_auth/-components/constants';
import { useTripBookingData } from '../-store';
import { CustomerHelp } from './customer-help';
import { ModifyBooking } from './modify-booking';
import { PartialPaymentCard } from './partial-payment-card';
import {
	RefundSummary,
	RefundTimeline,
	TransactionSummary,
} from './refund-info';

export function BookingDetailContent() {
	const bookingData = useTripBookingData();
	const hasScrolled = useRef(false);
	const { action } = useSearch({ from: '/_auth/trips/$bookingId/' });

	useEffect(() => {
		const scrollToSection =
			action === 'reschedule' ? 'modify-booking' : undefined;
		if (!scrollToSection || !bookingData || hasScrolled.current) return;
		hasScrolled.current = true;

		setTimeout(() => {
			const target = document.getElementById(scrollToSection);
			target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}, 300);
	}, [bookingData, action]);

	if (!bookingData) {
		return null;
	}
	const {
		farePolicies,
		cabDetail,
		extraPaymentInfo,
		refundDetail,
		rideDetails,
		contactDetails,
		cancellationDetail,
		rescheduleDetail,
		bookingDetail: { bookingStatus },
		parentBookingId,
		remainingAmountInfo,
		fareSummaryNote,
	} = bookingData;
	const { pricing: cabPricing } = cabDetail || {};
	const { amountToBePaid, displayFares } = extraPaymentInfo || {};
	const { charges, discounts, fareAdjustmentLineItems } = getFareLineItems(
		cabPricing,
		true,
	);
	const isSuccessBooking = SUCCESS_BOOKING_STATUS.includes(bookingStatus);
	const { paidCharges, toBePaidCharges } = cabPricing
		? getPartialPayFareRows(cabPricing, bookingStatus, { amountToBePaid })
		: { paidCharges: [], toBePaidCharges: [] };
	const showPartialPaymentCard =
		!!remainingAmountInfo &&
		remainingAmountInfo.amount > 0 &&
		bookingStatus === 'CONFIRMED';
	return (
		<Fragment>
			{refundDetail && (
				<div
					className="bg-primary p-20 flex flex-col gap-y-15"
					id="refund-status"
				>
					<SectionHeader title="Refund Status" />
					{refundDetail?.timeline && (
						<RefundTimeline timelineItems={refundDetail.timeline} />
					)}
					{refundDetail?.refundSummary && (
						<TransactionSummary refundSummary={refundDetail.refundSummary} />
					)}
				</div>
			)}
			{showPartialPaymentCard && remainingAmountInfo && (
				<PartialPaymentCard
					iconUrl={remainingAmountInfo.iconUrl}
					title={remainingAmountInfo.text}
					amount={remainingAmountInfo.amount}
					currency={cabPricing?.currency || 'INR'}
					subtext={remainingAmountInfo.subtext}
				/>
			)}
			{rideDetails && cabDetail && (
				<div className="bg-primary p-20 flex flex-col gap-y-15">
					<SectionHeader title="Booking Details" />
					<TripDetailsCard
						rideDetails={rideDetails}
						cabInfo={cabDetail}
						{...(bookingStatus === 'CANCELLED' && {
							mode: 'Collapsible',
							expanded: false,
						})}
					/>
				</div>
			)}
			{refundDetail?.breakUps && (
				<RefundSummary refundSummary={refundDetail.breakUps} />
			)}
			{cabPricing && (
				<FareSummary
					displayCharges={[...charges, ...(displayFares || [])]}
					discounts={discounts}
					fareAdjustmentLineItems={fareAdjustmentLineItems}
					paidCharges={paidCharges}
					toBePaidCharges={toBePaidCharges}
					mode="Collapsible"
					footnote={
						fareSummaryNote ? (
							<Typography variant="body-xs" className="text-secondary">
								{fareSummaryNote}
							</Typography>
						) : undefined
					}
				>
					{farePolicies && cabDetail && (
						<FarePolicies {...farePolicies} selectedCab={cabDetail} />
					)}
				</FareSummary>
			)}
			{contactDetails && (
				<ContactDetail
					mode="view"
					subtitle="We have sent the booking details on email & WhatsApp"
					contactDetails={contactDetails}
				/>
			)}
			{cabDetail && rideDetails && (
				<ModifyBooking
					id="modify-booking"
					cancellationDetail={cancellationDetail}
					rescheduleDetail={rescheduleDetail}
					cabDetail={cabDetail}
					rideDetails={rideDetails}
					parentBookingId={parentBookingId}
				/>
			)}
			{isSuccessBooking && (
				<div className="p-20 bg-primary">
					<CustomerHelp />
				</div>
			)}
		</Fragment>
	);
}
